import * as React from 'react';
import { useEffect, useState } from 'react';

import { Box, styled, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import axios from 'axios';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

import { RECORDING_INFORMATION } from '../../config/apiPaths';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { type ResponseRecordingInformation } from '../../types/recordingTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { processApiPath } from '../../utils/url';
import { GeneralDialog } from '../common/dialog/GeneralDialog';

export interface ViewAnalyticsDialogProps {
    recordingUuid?: string;
    open: boolean;
    handleClose: () => void;
    userUuid: string;
    classUuid: string;
}

const Label = styled(Typography)`
    font-weight: bold;
    margin-right: 1rem;
`;

export const ViewAnalyticsDialog = (props: ViewAnalyticsDialogProps) => {
    const { recordingUuid, open, handleClose, userUuid, classUuid } = props;
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState<ResponseRecordingInformation | undefined>();
    const { data: schoolInfo } = useSchoolInfo();

    useEffect(() => {
        if (!recordingUuid || !userUuid || !schoolInfo || !classUuid || !open) {
            return;
        }
        setInfo(undefined);
        const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
        const requestAndSetInfo = async () => {
            setLoading(true);
            const url = processApiPath(RECORDING_INFORMATION, {
                schoolUuid: schoolInfo?.uuid,
                classUuid,
                recordingUuid
            });
            try {
                const { data } = await axios.get<ResponseRecordingInformation>(url, {
                    headers: getUserUuidHeaders(userUuid)
                });
                setInfo({
                    ...data,
                    lastViewed: data.lastViewed
                        ? format(utcToZonedTime(data.lastViewed, timeZone), 'PPP pp')
                        : undefined
                });
                setLoading(false);
            } catch (e) {
                console.error(e);
                // TODO show error here.
            }
        };
        requestAndSetInfo();
    }, [recordingUuid, userUuid, schoolInfo, classUuid, t, open]);

    return (
        <GeneralDialog
            onClose={handleClose}
            open={open}
            isLoading={loading}
            title={t('lti:recordings.analytics')}
            actions={
                <Button autoFocus onClick={() => handleClose()} variant="contained" color="primary">
                    {t('common:close')}
                </Button>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Label>{t('lti:recordings.total-views')}:</Label>
                <Typography>{info?.totalViews}</Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row' }}>
                <Label>{t('lti:recordings.last-viewed')}:</Label>
                <Typography>{info?.lastViewed}</Typography>
            </Box>
        </GeneralDialog>
    );
};
